import PropTypes from "prop-types"
import classnames from "classnames"
import Chevron from "shared/components/icons/chevron"
import { capitalize } from "humanize-plus"
import GearIcon from "images/partners/gear.svg?raw"
import parse from "html-react-parser"

export const orgShape = PropTypes.shape({
  name: PropTypes.string,
  id: PropTypes.number,
  tier: PropTypes.string,
  services: PropTypes.array,
})

export const serviceShape = PropTypes.shape({
  module_name: PropTypes.string,
  name: PropTypes.string,
  icon: PropTypes.string,
  color_hsl: PropTypes.string,
  published: PropTypes.bool,
  organization: orgShape,
  url: PropTypes.string,
})

const ChevronSelector = ({ rotate, toggleOpen }) => (
  <div className="chevron-click-handler" onClick={toggleOpen}>
    <div className="flex-holder">
      <Chevron color="white" direction={rotate ? "up" : "down"} />
    </div>
  </div>
)

ChevronSelector.propTypes = {
  rotate: PropTypes.bool,
  toggleOpen: PropTypes.func,
}

export const ServiceListElement = ({
  service,
  shouldRenderChevron,
  isMakerService,
  menuIsOpen,
  toggleOpen,
  handleMenuClick,
}) => (
  <li
    className={classnames("service-name", { maker: isMakerService })}
    key={service.name}
    onClick={e => {
      if (isMakerService) return
      e.preventDefault()
      // Are they holding command and wanting to open this in a new tab? Let 'em.
      if (e.metaKey) {
        return window.open(e.target.href, "_blank")
      }
      handleMenuClick(service)
    }}
  >
    <div className="service-name-wrapper">
      <div className="service-name-link-container">
        {!isMakerService && (
          <img
            className="service-icon"
            src={service.icon_on_color || service.icon}
            alt={service.name}
            title={service.name}
          />
        )}
        <a href={service.url} title={`Go to the ${service.name} Service`}>
          <span className="name">{service.name}</span>
          {!isMakerService && (
            <span className="published-status">{`${
              service.published ? "P" : "Unp"
            }ublished service`}</span>
          )}
        </a>
      </div>
    </div>
    {shouldRenderChevron && (
      <ChevronSelector rotate={menuIsOpen} toggleOpen={toggleOpen} />
    )}
  </li>
)

ServiceListElement.propTypes = {
  service: PropTypes.object,
  shouldRenderChevron: PropTypes.bool,
  isMakerService: PropTypes.bool,
  menuIsOpen: PropTypes.bool,
  toggleOpen: PropTypes.func,
  handleMenuClick: PropTypes.func,
}

const TierDisplayName = ({ org: { tier, tier_display_name } }) => {
  if (!tier) {
    return null
  }

  const tierClasses = classnames("tier", tier)

  return (
    <span className={tierClasses}>
      {tier === "free"
        ? "Your organization"
        : `${capitalize(tier_display_name)} organization`}
    </span>
  )
}

export const OrganizationMenuItem = ({
  org,
  shouldRenderChevron,
  isActiveOrg,
  menuIsOpen,
  toggleOpen,
  handleMenuClick,
}) => {
  const orgSelectorClasses = classnames("organization-name-wrapper", {
    "light-overlay": isActiveOrg || !menuIsOpen,
    "with-no-service": org.services.length === 0,
  })

  return (
    <li key={org.id} onClick={() => handleMenuClick(org)}>
      <div className={orgSelectorClasses}>
        <div className="name-link-container">
          <span className="organization-name">{org.name}</span>
          <TierDisplayName org={org} />
          {menuIsOpen && <OrganizationAdminLink org={org} />}
        </div>
        {shouldRenderChevron && (
          <ChevronSelector rotate={menuIsOpen} toggleOpen={toggleOpen} />
        )}
      </div>
    </li>
  )
}

OrganizationMenuItem.propTypes = {
  org: orgShape,
  shouldRenderChevron: PropTypes.bool,
  isActiveOrg: PropTypes.bool,
  menuIsOpen: PropTypes.bool,
  toggleOpen: PropTypes.func,
  handleMenuClick: PropTypes.func,
}

const OrganizationAdminLink = ({ org }) => {
  if (org.id > 0) {
    return (
      <a
        href={org.admin_uri}
        title={`Manage the ${org.name} Organization`}
        className="organization-admin-link"
        onClick={e => e.stopPropagation()}
      >
        <div className="link-description">
          <span>{parse(GearIcon)}</span>
          <span>Settings</span>
        </div>
      </a>
    )
  }
  return null
}

OrganizationAdminLink.propTypes = {
  org: orgShape,
}
